import {
    Button,
    Popover,
    requiredRule,
    useActionsBtnContext,
    useErrorState,
    useToastContext,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { BODatePicker } from 'base-components/BODatePicker/BODatePicker';
import { faBalanceScale } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { billRollback, billRollup, getRollbackInfoInfo } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { BillRollbackInfo } from 'models/api/order';
import { useNotifyContext } from 'providers/NotifyProvider';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

const UndoRedoPopover = ({
    statusCode,
    isLoanInRollbackState,
    setRollbackState,
    isDefaultRollbackDt,
    id,
}: {
    statusCode: string;
    isLoanInRollbackState: boolean;
    setRollbackState: (value: boolean) => void;
    isDefaultRollbackDt: boolean;
    id: number;
}) => {
    const api = useApi();
    const { hasRole } = useAuth();
    const { t } = useTranslation();

    const { addRequest } = useActionsBtnContext();
    const { handleRequestError } = useErrorState();
    const { addToast } = useToastContext();
    const { addSubscription, removeSubscription } = useNotifyContext();
    const [wasOpened, setWasOpened] = useState(false);
    const [rollbackInfo, setRollbackInfo] = useState<BillRollbackInfo | null>(null);
    const queryClient = useQueryClient();

    const rollbackInfoQuery = useQuery(
        [`rollbackInfo${id}`, wasOpened],
        () => (wasOpened ? getRollbackInfoInfo(api, id) : null),
        {
            onSuccess: (data) => setRollbackInfo(data),
        }
    );

    const rollupMutation = useMutation('rollupMutation', () => billRollup(api, id), {
        onError: (err: AxiosError<AxiosErrorResponse>) => handleRequestError(err),
        retry: false,
    });

    useEffect(() => {
        addSubscription({
            key: 'onBillRolling',
            type: 'RollBackBillFinish',
            callback: (message) => {
                const res = JSON.parse(message);
                addToast({
                    type: 'info',
                    description: res.Err || t('BackOfficeBillingSuccessRolledBack'),
                });
                if (!res.Err) {
                    queryClient.invalidateQueries('orderInfo');
                    setRollbackState(true);
                }
            },
        });

        addSubscription({
            key: 'onBillRolling',
            type: 'RollUpBillFinish',
            callback: (message) => {
                const res = JSON.parse(message);
                addToast({
                    type: 'info',
                    description: res.Err || t('BackOfficeBillingSuccessRolledUp'),
                });
                if (!res.Err) {
                    queryClient.invalidateQueries('orderInfo');
                    setRollbackState(false);
                }
            },
        });

        return () => {
            removeSubscription('onBillRolling');
        };
    }, []);

    //TODO CustomValidatedDatePicker
    return (
        <>
            {statusCode !== 'cancel' &&
                isDefaultRollbackDt &&
                (isLoanInRollbackState
                    ? hasRole('orderBillRollup') && (
                          <div>
                              <Button
                                  type="button"
                                  className="popover-btn"
                                  onClick={() => {
                                      addRequest({
                                          content: t('BackOfficeAreYouSure'),
                                          confirmCallback: (res) => {
                                              res && rollupMutation.mutate();
                                          },
                                      });
                                  }}
                              >
                                  <FontAwesomeIcon
                                      icon={faBalanceScale}
                                      color="success"
                                      className="text-success order-action-btn__icon"
                                  />
                                  <span>
                                      <Trans i18nKey={'BackOfficeBillingRollup'} />
                                  </span>
                              </Button>
                          </div>
                      )
                    : hasRole('orderGetBillRollbackInfo') && (
                          <Popover
                              label="rollback"
                              textStyle="danger"
                              buttonIcon={faBalanceScale}
                              buttonLabel="BackOfficeBillingRollback"
                              refetchQueryKeys={null}
                              header={
                                  <>
                                      <Trans i18nKey="BackOfficeBillingRollback" />
                                      {` #${id}`}
                                  </>
                              }
                              mutationFunction={(data) => {
                                  if (rollbackInfo)
                                      return billRollback(api, { ...rollbackInfo, ...data });
                                  else throw new Error('Rollback info is empty');
                              }}
                              onToggleCallback={() => setWasOpened((prev) => !prev)}
                              canSave={hasRole('orderBillRollback')}
                          >
                              <BODatePicker
                                  id="info-birthday-field"
                                  value={rollbackInfo?.rollbackDt ?? new Date()}
                                  onChange={(date) =>
                                      setRollbackInfo((prev) =>
                                          prev ? { ...prev, rollbackDt: date } : prev
                                      )
                                  }
                                  inputProps={{ label: t('BackOfficeBillingRollbackDt') }}
                              />
                              <BODatePicker
                                  id="info-birthday-field"
                                  value={rollbackInfo?.startBillDt ?? new Date()}
                                  onChange={(date) =>
                                      setRollbackInfo((prev) =>
                                          prev ? { ...prev, startBillDt: date } : prev
                                      )
                                  }
                                  inputProps={{ label: t('BackOfficeBillingRollupDt') }}
                              />
                              <ValidatedInput
                                  name="dsc"
                                  type="textarea"
                                  label={t('BackOfficeComment')}
                                  loading={rollbackInfoQuery.isFetching}
                                  rules={{
                                      required: requiredRule,
                                  }}
                              />
                          </Popover>
                      ))}
        </>
    );
};

export default UndoRedoPopover;
